import axios from "axios";
import { SharedFile, SharedFileInput } from "./types/file";
import { BaseRecord, FormConfig, Member, Team } from "./types/form";
import { AppField, Filter, SimpleRecord } from "@smartsuite/types";
import { env } from "./utils/env";
import { VIEW_AS_HEADER, getViewAsParam } from "./utils/viewAs";

function baseUrl(): string {
  return env.REACT_APP_SMARTSUITE_API_URL;
}

function makeHeaders(accountId: string, passcode?: string | null): Record<string, string> {
  const headers: Record<string, string> = { "ACCOUNT-ID": accountId };
  if (passcode) {
    headers["SHARED-REPORT-PASSWORD"] = passcode;
  }
  // Forward 'view as' impersonation when the host app embedded this form with it (internal forms
  // only). The backend ignores it for anonymous/public forms, so it is safe to always include.
  const viewAs = getViewAsParam();
  if (viewAs) {
    headers[VIEW_AS_HEADER] = viewAs;
  }
  return headers;
}

export async function fetchFormConfig(
  accountId: string,
  hash: string,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<FormConfig> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function saveFormRecord(
  accountId: string,
  hash: string,
  record: BaseRecord,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<void> {
  return await axios
    .post(`${baseUrl()}/api/v1/forms/${hash}/create_record/`, record, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function createSharedFiles(
  accountId: string,
  hash: string,
  files: SharedFileInput[],
  isPreview: boolean = false,
  passcode?: string | null
): Promise<SharedFile[]> {
  return await axios
    .post(`${baseUrl()}/api/v1/forms/${hash}/create_shared_files/`, files, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function getLinkedRecords(
  accountId: string,
  hash: string,
  appId: string,
  fieldSlug: string,
  fieldsInUse: Set<string>,
  filter: Filter | null = null,
  isPreview: boolean = false,
  passcode?: string | null,
  ids?: string[]
): Promise<SimpleRecord[]> {
  return await axios
    .post(
      `${baseUrl()}/api/v1/forms/${hash}/linked_records/`,
      {
        app: appId,
        slug: fieldSlug,
        filter_data: filter,
        isPreview,
        fields: Array.from(fieldsInUse),
        ids,
      },
      {
        headers: makeHeaders(accountId, passcode),
        params: {
          isPreview,
        },
      }
    )
    .then((res) => res.data.map((record: SimpleRecord) => ({ ...record, id: record._id })));
}

export async function getLinkedApplicationFields(
  accountId: string,
  linkedAppId: string,
  hash: string,
  fieldsInUse: Set<string>,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<{ [key: string]: AppField }> {
  return await axios
    .get(
      `${baseUrl()}/api/v1/forms/${hash}/linked_application_fields/?linked_app_id=${linkedAppId}`,
      {
        headers: makeHeaders(accountId, passcode),
        params: {
          isPreview,
          fields: Array.from(fieldsInUse),
        },
      }
    )
    .then((res) => res.data);
}

type GetRelatedRecordsParams = {
  accountId: string;
  linkedAppId: string;
  hash: string;
  visibleFields: string[];
  isPreview?: boolean;
  passcode?: string | null;
};

export async function getRelatedRecords({
  accountId,
  linkedAppId,
  hash,
  visibleFields,
  isPreview = false,
  passcode = null,
}: GetRelatedRecordsParams): Promise<BaseRecord[]> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/related_records/?linked_app_id=${linkedAppId}`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
        visible_fields: visibleFields,
      },
    })
    .then((res) => res.data);
}

export async function getAssignedToMembers(
  accountId: string,
  hash: string,
  fieldSlug: string,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<Member[]> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/assigned_to_values/?slug=${fieldSlug}`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function getChecklistAssigneeMembers(
  accountId: string,
  hash: string,
  fieldSlug: string,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<Member[]> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/checklist_assignee_values/?slug=${fieldSlug}`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function getChecklistAssigneeTeams(
  accountId: string,
  hash: string,
  fieldSlug: string,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<Team[]> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/checklist_assignee_teams/?slug=${fieldSlug}`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

export async function getTeamValues(
  accountId: string,
  hash: string,
  fieldSlug: string,
  isPreview: boolean = false,
  passcode?: string | null
): Promise<Team[]> {
  return await axios
    .get(`${baseUrl()}/api/v1/forms/${hash}/team_values/?slug=${fieldSlug}`, {
      headers: makeHeaders(accountId, passcode),
      params: {
        isPreview,
      },
    })
    .then((res) => res.data);
}

function internalBaseUrl(reportId: string): string {
  return `${baseUrl()}/api/v1/forms/internal/${reportId}`;
}

export async function fetchInternalFormConfig(
  accountId: string,
  reportId: string
): Promise<FormConfig> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
    })
    .then((res) => res.data);
}

export async function saveInternalFormRecord(
  accountId: string,
  reportId: string,
  record: Record<string, unknown>
): Promise<void> {
  return await axios
    .post(`${internalBaseUrl(reportId)}/create_record/`, record, {
      headers: makeHeaders(accountId),
      withCredentials: true,
    })
    .then((res) => res.data);
}

export async function getInternalLinkedRecords(
  accountId: string,
  reportId: string,
  appId: string,
  fieldSlug: string,
  fieldsInUse: Set<string>,
  filter: Filter | null = null,
  ids?: string[]
): Promise<SimpleRecord[]> {
  return await axios
    .post(
      `${internalBaseUrl(reportId)}/linked_records/`,
      {
        app: appId,
        slug: fieldSlug,
        filter_data: filter,
        fields: Array.from(fieldsInUse),
        ids,
      },
      {
        headers: makeHeaders(accountId),
        withCredentials: true,
      }
    )
    .then((res) => res.data.map((record: SimpleRecord) => ({ ...record, id: record._id })));
}

export async function getInternalLinkedApplicationFields(
  accountId: string,
  reportId: string,
  linkedAppId: string,
  fieldsInUse: Set<string>
): Promise<{ [key: string]: AppField }> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/linked_application_fields/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        linked_app_id: linkedAppId,
        "fields[]": Array.from(fieldsInUse),
      },
    })
    .then((res) => res.data);
}

export async function getInternalAssignedToMembers(
  accountId: string,
  reportId: string,
  fieldSlug: string
): Promise<Member[]> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/assigned_to_values/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        slug: fieldSlug,
      },
    })
    .then((res) => res.data);
}

export async function getInternalChecklistAssigneeMembers(
  accountId: string,
  reportId: string,
  fieldSlug: string
): Promise<Member[]> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/checklist_assignee_values/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        slug: fieldSlug,
      },
    })
    .then((res) => res.data);
}

export async function getInternalChecklistAssigneeTeams(
  accountId: string,
  reportId: string,
  fieldSlug: string
): Promise<Team[]> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/checklist_assignee_teams/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        slug: fieldSlug,
      },
    })
    .then((res) => res.data);
}

export async function getInternalTeamValues(
  accountId: string,
  reportId: string,
  fieldSlug: string
): Promise<Team[]> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/team_values/`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        slug: fieldSlug,
      },
    })
    .then((res) => res.data);
}

type GetInternalRelatedRecordsParams = {
  accountId: string;
  reportId: string;
  linkedAppId: string;
  visibleFields: string[];
};

export async function getInternalRelatedRecords({
  accountId,
  reportId,
  linkedAppId,
  visibleFields,
}: GetInternalRelatedRecordsParams): Promise<BaseRecord[]> {
  return await axios
    .get(`${internalBaseUrl(reportId)}/related_records/?linked_app_id=${linkedAppId}`, {
      headers: makeHeaders(accountId),
      withCredentials: true,
      params: {
        visible_fields: visibleFields,
      },
    })
    .then((res) => res.data);
}

export async function createInternalSharedFiles(
  accountId: string,
  reportId: string,
  files: SharedFileInput[]
): Promise<SharedFile[]> {
  return await axios
    .post(
      `${internalBaseUrl(reportId)}/create_shared_files/`,
      { files },
      {
        headers: makeHeaders(accountId),
        withCredentials: true,
      }
    )
    .then((res) => res.data);
}
